import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import {
  AccountManagerOption,
  UNASSIGNED_ACCOUNT_MANAGER_OPTION,
  useSearchAccountManagerCandidatesApi,
} from 'apis/restaurants/use-search-account-manager-candidates-api';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  accountManager: AccountManagerOption | undefined;
};

const defaultValues: TInputs = {
  accountManager: undefined,
};

// Header filter for the Restaurants list: a user id or "unassigned" in the `accountManagerId`
// URL param, which the UMS list endpoint understands as-is.
export const AccountManagerFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const { loadOptions } = useSearchAccountManagerCandidatesApi({ includeUnassigned: true });

  useEffect(() => {
    if (params.accountManagerId) setIsActive.on();
    else setIsActive.off();
  }, [params.accountManagerId]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Restore the selection from the URL: a user id restored from the URL is shown as "#id" (its
  // name is only known after a new selection); "unassigned" restores its full label.
  const updateValues = () => {
    const current = params.accountManagerId as string | undefined;
    if (!current) {
      setValue('accountManager', undefined);
    } else if (current === 'unassigned') {
      setValue('accountManager', UNASSIGNED_ACCOUNT_MANAGER_OPTION);
    } else {
      setValue('accountManager', { value: Number(current), label: `#${current}` });
    }
  };

  useEffect(() => {
    updateValues();
  }, [params.accountManagerId]);

  const onSubmit = (data: TInputs) => {
    params.accountManagerId = data.accountManager ? String(data.accountManager.value) : null;

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const isFilterDisabled = Object.keys(formState.dirtyFields).length === 0;

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='accountManager'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Account Manager</FormLabel>
                <ChakraAsyncSelect
                  id='accountManager'
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  loadOptions={loadOptions}
                  cacheOptions
                  isClearable
                  placeholder='Search active CMS users'
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={isFilterDisabled}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
